/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

import React from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { PDFDownloadLink } from '@react-pdf/renderer';
import ExportAnswers from '../../../../shared-components/ExportAnswers';
//
import useExport from '../../../../actions/useExport';
// BASE components
import BASESvgIcon from '../../../../../@BASE/core/BASESvgIcon';
import BASELoading from '../../../../../@BASE/core/BASELoading';
// Material UI
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
// custom components
import ExportButton from '../../../../shared-components/ExportButton';

export default function Export(props) {
  const { resource, instance } = useOutletContext();

  const {
    exportData,
    processedData,
    metaData,
    loading,
    loadingPhase,
    loadingProgress,
    error
  } = useExport(resource, instance);

  const stripHtml = (html) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  const exportMedia = () => {
    const media = metaData?.media;

    if (!Array.isArray(media) || media.length === 0) {
      console.warn('No media available to export.');
      return;
    }

    const audio = [];
    const video = [];
    const image = [];
    const pdf = [];
    const unknown = [];

    media.forEach((item) => {
      if (item.type === 'audio') {
        audio.push(item);
      } else if (item.type === 'video') {
        video.push(item);
      } else if (item.type === 'image') {
        image.push(item);
      } else if (item.type === 'pdf') {
        pdf.push(item);
      } else {
        unknown.push(item);
      }
    });

    if (unknown.length > 0) {
      console.warn(
        `There is ${unknown.length} objects found of an unknown type!!!`
      );
    }

    const audioSheetData = getAudioSheetData(audio);
    const videoSheetData = getVideoSheetData(video);
    const imageSheetData = getImageSheetData(image);
    const pdfSheetData = getPDFSheetData(pdf);

    const audioWorksheet = XLSX.utils.aoa_to_sheet(audioSheetData);
    const videoWorksheet = XLSX.utils.aoa_to_sheet(videoSheetData);
    const imageWorksheet = XLSX.utils.aoa_to_sheet(imageSheetData);
    const pdfWorksheet = XLSX.utils.aoa_to_sheet(pdfSheetData);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, audioWorksheet, 'Audios');
    XLSX.utils.book_append_sheet(workbook, videoWorksheet, 'Videos');
    XLSX.utils.book_append_sheet(workbook, imageWorksheet, 'Images');
    XLSX.utils.book_append_sheet(workbook, pdfWorksheet, 'PDFs');

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array'
    });
    const dataBlob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const now = new Date();
    const dateStr = `${now.getMonth() + 1}-${now.getDate()}-${now
      .getFullYear()
      .toString()
      .slice(-2)}`;
    saveAs(dataBlob, `media-${exportData.label}-${dateStr}.xlsx`);
  };

  const getAudioSheetData = (audio) => {
    // Define header labels and order
    const headers = [
      'ID',
      'SRC',
      'Description',
      'Title, EN',
      'Title, TL',
      'Transcript',
      'Page ID',
      'Author',
      'Author Desc, EN',
      'Author Desc, TL',
      'CC License',
      'Published Date',
      'Downloaded Date',
      'Media URL',
      'Source URL'
    ];
    const keys = [
      'id',
      'src',
      'description',
      'title_en',
      'title_tl',
      'transcript',
      'page',
      'author',
      'author_desc_en',
      'author_desc_tl',
      'cc_license',
      'published_date',
      'downloaded_date',
      'media_url',
      'source_url'
    ];

    // Build data with custom column order
    const rows = audio.map((item) => ({
      id: item.id,
      linked_item: item.linked_item,
      src: item.src,
      description: item.description?.trim() || 'N/A',
      title_en: item.title_en,
      title_tl: item.title_tl,
      transcript: stripHtml(item.transcript).trim() || 'Missing Transcript',
      page: item.page,
      author: item.author,
      author_desc_en: item.author_desc_en?.trim() || 'N/A',
      author_desc_tl: item.author_desc_tl?.trim() || 'N/A',
      cc_license: item.cc_license,
      published_date: item.published_date
        ? new Date(item.published_date).toLocaleString('en-US', {
            timeZone: 'UTC',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          }) + ' UTC'
        : '',
      downloaded_date: item.downloaded_date
        ? new Date(item.downloaded_date).toLocaleString('en-US', {
            timeZone: 'UTC',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          }) + ' UTC'
        : '',
      media_url: item.media_url,
      source_url: item.source_url
    }));

    // Start with a manual header row
    const sheetData = [
      headers,
      ...rows.map((obj) =>
        keys.map((k) => obj[headers[keys.indexOf(k)]] || obj[k])
      )
    ];

    return sheetData;
  };

  const getVideoSheetData = (video) => {
    // Define header labels and order
    const headers = [
      'ID',
      'SRC',
      'Description',
      'Title, EN',
      'Title, TL',
      'Transcript',
      'Page ID',
      'Author',
      'Author Desc, EN',
      'Author Desc, TL',
      'CC License',
      'Published Date',
      'Downloaded Date',
      'Media URL',
      'Source URL'
    ];
    const keys = [
      'id',
      'src',
      'description',
      'title_en',
      'title_tl',
      'transcript',
      'page',
      'author',
      'author_desc_en',
      'author_desc_tl',
      'cc_license',
      'published_date',
      'downloaded_date',
      'media_url',
      'source_url'
    ];

    // Build data with custom column order
    const rows = video.map((item) => ({
      id: item.id,
      linked_item: item.linked_item,
      src: item.src,
      description: item.description?.trim() || 'N/A',
      title_en: item.title_en,
      title_tl: item.title_tl,
      transcript: stripHtml(item.transcript).trim() || 'Missing Transcript',
      page: item.page,
      author: item.author,
      author_desc_en: item.author_desc_en?.trim() || 'N/A',
      author_desc_tl: item.author_desc_tl?.trim() || 'N/A',
      cc_license: item.cc_license,
      published_date: item.published_date
        ? new Date(item.published_date).toLocaleString('en-US', {
            timeZone: 'UTC',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          }) + ' UTC'
        : '',
      downloaded_date: item.downloaded_date
        ? new Date(item.downloaded_date).toLocaleString('en-US', {
            timeZone: 'UTC',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          }) + ' UTC'
        : '',
      media_url: item.media_url,
      source_url: item.source_url
    }));

    // Start with a manual header row
    const sheetData = [
      headers,
      ...rows.map((obj) =>
        keys.map((k) => obj[headers[keys.indexOf(k)]] || obj[k])
      )
    ];

    return sheetData;
  };

  const getImageSheetData = (image) => {
    // Define header labels and order
    const headers = [
      'ID',
      'SRC',
      'Description',
      'Title, EN',
      'Title, TL',
      'Page ID',
      'Author',
      'CC License',
      'Published Date',
      'Downloaded Date',
      'Source URL',
      'Size'
    ];
    const keys = [
      'id',
      'src',
      'description',
      'title_en',
      'title_tl',
      'page',
      'author',
      'cc_license',
      'published_date',
      'downloaded_date',
      'source_url',
      'size'
    ];

    // NOTES ON THE SIZE THAT WAS PROVIDED
    // Reading is 900 x 900
    // summary, enrichment, assessment intro/summary and debriefs are also 900 x 900
    // everything else is 1920 x 1080
    // the items in the PE/FPE, those are all 960 x 540

    // Build data with custom column order
    const rows = image.map((item) => ({
      id: item.id,
      linked_item: item.linked_item,
      src: item.src,
      description: stripHtml(item.description),
      title_en: item.title_en,
      title_tl: item.title_tl,
      page: item.page,
      author: item.author,
      cc_license: item.cc_license,
      published_date: item.published_date
        ? new Date(item.published_date).toLocaleString('en-US', {
            timeZone: 'UTC',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          }) + ' UTC'
        : '',
      downloaded_date: item.downloaded_date
        ? new Date(item.downloaded_date).toLocaleString('en-US', {
            timeZone: 'UTC',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          }) + ' UTC'
        : '',
      source_url: item.source_url,
      size: item.size ? item.size : ''
    }));

    // Start with a manual header row
    const sheetData = [
      headers,
      ...rows.map((obj) =>
        keys.map((k) => obj[headers[keys.indexOf(k)]] || obj[k])
      )
    ];

    return sheetData;
  };

  const getPDFSheetData = (pdf) => {
    // Define header labels and order
    const headers = [
      'ID',
      'Title, EN',
      'Title, TL',
      'Page ID',
      'Author',
      'CC License',
      'Published Date',
      'Downloaded Date',
      'Source URL'
    ];
    const keys = [
      'id',
      'title_en',
      'title_tl',
      'page',
      'author',
      'cc_license',
      'published_date',
      'downloaded_date',
      'source_url'
    ];

    // Build data with custom column order
    const rows = pdf.map((item) => ({
      id: item.id,
      linked_item: item.linked_item,
      title_en: item.title_en,
      title_tl: item.title_tl,
      page: item.page,
      author: item.author,
      cc_license: item.cc_license,
      published_date: item.published_date
        ? new Date(item.published_date).toLocaleString('en-US', {
            timeZone: 'UTC',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          }) + ' UTC'
        : '',
      downloaded_date: item.downloaded_date
        ? new Date(item.downloaded_date).toLocaleString('en-US', {
            timeZone: 'UTC',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          }) + ' UTC'
        : '',
      source_url: item.source_url
    }));

    // Start with a manual header row
    const sheetData = [
      headers,
      ...rows.map((obj) =>
        keys.map((k) => obj[headers[keys.indexOf(k)]] || obj[k])
      )
    ];

    return sheetData;
  };

  const exportFocusPoints = () => {
    const focusPoints = metaData?.focusPoints;

    if (!Array.isArray(focusPoints) || focusPoints.length === 0) {
      console.warn('No focus points available to export.');
      return;
    }

    const mediaFocusPoints = [];
    const textFocusPoints = [];

    focusPoints.forEach((fp) => {
      if (
        Number.isInteger(fp.start_minute) &&
        Number.isInteger(fp.start_second)
      ) {
        mediaFocusPoints.push(fp);
      } else {
        textFocusPoints.push(fp);
      }
    });

    const mediaWorksheet = mediaFocusSheetData(mediaFocusPoints);
    const textWorksheet = textFocusSheetData(textFocusPoints);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      mediaWorksheet,
      'Media Focus Points'
    );
    XLSX.utils.book_append_sheet(workbook, textWorksheet, 'Text Focus Points');

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array'
    });
    const dataBlob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const now = new Date();
    const dateStr = `${now.getMonth() + 1}-${now.getDate()}-${now
      .getFullYear()
      .toString()
      .slice(-2)}`;
    saveAs(dataBlob, `focus_points-${exportData.label}-${dateStr}.xlsx`);
  };

  const textFocusSheetData = (textFocusPoints) => {
    // Define header labels and order
    const headers = ['Linked Item', 'Header', 'Content', 'ID', 'Page'];
    const keys = ['linked_item', 'focus_header', 'focus_content', 'id', 'page'];

    // Build data with custom column order
    const rows = textFocusPoints.map((item) => ({
      linked_item: item.linked_item,
      focus_header: item.focus_header,
      focus_content: stripHtml(item.focus_content),
      id: item.id,
      page: item.page
    }));

    // Start with a manual header row
    const sheetData = [
      headers,
      ...rows.map((obj) =>
        keys.map((k) => obj[headers[keys.indexOf(k)]] || obj[k])
      )
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

    // Convert 'Page' column values into clickable hyperlinks
    rows.forEach((item, index) => {
      const rowIndex = index + 1; // +1 to skip header row
      const colIndex = 4; // 'Page' is the 5th column (0-indexed)

      const pageId = item.page;
      if (pageId) {
        const url = `http://localhost:3007/pages/${pageId}`;
        const cellAddress = XLSX.utils.encode_cell({
          r: rowIndex,
          c: colIndex
        });
        worksheet[cellAddress] = {
          t: 's',
          f: `HYPERLINK("${url}", "${pageId}")`
        };
      }
    });

    return worksheet;
  };

  const mediaFocusSheetData = (mediaFocusPoints) => {
    // Define header labels and order
    const headers = ['Title', 'Start', 'End', 'Content', 'ID', 'Page'];
    const keys = ['title', 'start', 'end', 'content', 'id', 'page'];

    // the following will help ensure that seconds say '05' instead of '5' while leaving '12' as '12'
    const pad = (n) => String(n).padStart(2, '0');

    // Build data with custom column order
    const rows = mediaFocusPoints.map((item) => ({
      title: item.title,
      start: `${item.start_minute}:${pad(item.start_second)}`,
      end: `${item.end_minute}:${pad(item.end_second)}`,
      content: stripHtml(item.content),
      id: item.id,
      page: item.page
    }));

    // Start with a manual header row
    const sheetData = [
      headers,
      ...rows.map((obj) =>
        keys.map((k) => obj[headers[keys.indexOf(k)]] || obj[k])
      )
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

    // Convert 'Page' column values into clickable hyperlinks
    rows.forEach((item, index) => {
      const rowIndex = index + 1; // +1 to skip header row
      const colIndex = 5; // 'Page' is the 6th column (0-indexed)

      const pageId = item.page;
      if (pageId) {
        const url = `http://localhost:3007/pages/${pageId}`;
        const cellAddress = XLSX.utils.encode_cell({
          r: rowIndex,
          c: colIndex
        });
        worksheet[cellAddress] = {
          t: 's',
          f: `HYPERLINK("${url}", "${pageId}")`
        };
      }
    });

    return worksheet;
  };

  const exportEMentors = () => {
    const EMentors = metaData?.eMentor;

    if (!Array.isArray(EMentors) || EMentors.length === 0) {
      console.warn('No eMentors available to export.');
      return;
    }

    // Define header labels and order
    const headers = ['Index', 'Title', 'Content', 'Source', 'Page', 'ID'];
    const keys = ['index', 'title', 'content', 'source', 'page', 'id'];

    // Build data with custom column order
    const rows = EMentors.map((item, index) => ({
      Index: index + 1,
      Title: item.title,
      Content: stripHtml(item.content),
      Source: item.source,
      page: item.page,
      ID: item.id
    }));

    // Start with a manual header row
    const sheetData = [
      headers,
      ...rows.map((obj) =>
        keys.map((k) => obj[headers[keys.indexOf(k)]] || obj[k])
      )
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

    // Convert 'Page' column values into clickable hyperlinks
    rows.forEach((item, index) => {
      const rowIndex = index + 1; // +1 to skip header row
      const colIndex = 4; // 'Page' is the 5th column (0-indexed)

      const pageId = item.page;
      if (pageId) {
        const url = `http://localhost:3007/pages/${pageId}`;
        const cellAddress = XLSX.utils.encode_cell({
          r: rowIndex,
          c: colIndex
        });
        worksheet[cellAddress] = {
          t: 's',
          f: `HYPERLINK("${url}", "${pageId}")`
        };
      }
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'eMentor Notes');

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array'
    });
    const dataBlob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const now = new Date();
    const dateStr = `${now.getMonth() + 1}-${now.getDate()}-${now
      .getFullYear()
      .toString()
      .slice(-2)}`;
    saveAs(dataBlob, `eMentors-${exportData.label}-${dateStr}.xlsx`);
  };

  if (loading) {
    return (
      <div className="relative flex flex-col flex-auto px-24 sm:px-48 h-screen justify-center">
        <div className="flex flex-col">
          <BASELoading />
          <LinearProgress color="secondary" className="mt-64 mb-24" />
          <Typography
            className="text-md font-medium text-center"
            color="text.secondary"
          >
            {loadingPhase}
          </Typography>
        </div>
      </div>
    );
  }

  console.log('Processed Data:', processedData);
  console.log('exportData:', exportData);

  return (
    <div className="relative flex flex-col flex-auto mt-64 px-24 sm:px-48">
      <Typography className="mt-12 text-2xl font-bold truncate">
        Export
      </Typography>
      <Divider className="mt-16 mb-24" />
      <div className="mt-16 grid grid-cols-12 gap-x-4">
        <Typography
          className="col-span-10 text-md font-medium"
          color="text.secondary"
        >
          DATA TYPE
        </Typography>
        <Typography
          className="col-span-2 text-right text-md font-medium"
          color="text.secondary"
        >
          DOWNLOAD
        </Typography>

        <div className="col-span-12 my-16 border-b" />

        <div className="col-span-8">
          <Typography className="text-lg font-medium">Content</Typography>
          <Typography className="mt-8 text-md" color="text.secondary">
            Gathers all of the lesson data and exports it to a downloadable JSON
            file. This file is ready to be added to the SCO Framework.
          </Typography>
        </div>
        <div className="col-span-4 text-right self-center">
          <ExportButton
            data={{
              ...processedData,
              materials: metaData.materials,
              images: metaData.images
            }}
          />
        </div>

        <div className="col-span-12 my-16 border-b" />

        <div className="col-span-8">
          <Typography className="text-lg font-medium">Storyboard</Typography>
          <Typography className="mt-8 text-md" color="text.secondary">
            Gathers all of the lesson data and converts it to a PDF ready
            format.
          </Typography>
        </div>
        <div className="col-span-4 text-right self-center">
          <Button
            variant="outlined"
            component={Link}
            to={`/lessons/${instance.id}/storyboard`}
            endIcon={
              <BASESvgIcon className="" size={20}>
                heroicons-solid:download
              </BASESvgIcon>
            }
          >
            PDF
          </Button>
        </div>

        <div className="col-span-12 my-16 border-b" />

        <div className="col-span-8">
          <Typography className="text-lg font-medium">Answer Key</Typography>
          <Typography className="mt-8 text-md" color="text.secondary">
            Gathers all of the lesson question and answer data and converts it
            to a PDF ready format.
          </Typography>
        </div>
        <div className="col-span-4 text-right self-center">
          <Button
            variant="outlined"
            component={Link}
            to={`/lessons/${instance.id}/answers`}
            endIcon={
              <BASESvgIcon className="" size={20}>
                heroicons-solid:download
              </BASESvgIcon>
            }
          >
            PDF
          </Button>
          {/* <div className="col-span-4 text-right self-center">
            <PDFDownloadLink
              document={<ExportAnswers exportData={exportData} />}
              fileName={`${
                exportData?.course?.language?.acronym || 'missing-acronym'
              } - Answer Key - ${exportData?.title || 'missing-title'}.pdf`}
              style={{
                textDecoration: 'none',
                color: 'inherit'
              }}
            >
              {({ loading }) => (
                <Button
                  variant="outlined"
                  endIcon={
                    <BASESvgIcon className="" size={20}>
                      heroicons-solid:download
                    </BASESvgIcon>
                  }
                >
                  {loading ? 'Preparing...' : 'PDF'}
                </Button>
              )}
            </PDFDownloadLink>
          </div> */}
        </div>

        <div className="col-span-12 my-16 border-b" />

        <div className="col-span-8">
          <Typography className="text-lg font-medium">Media</Typography>
          <Typography className="mt-8 text-md" color="text.secondary">
            Gathers all of the lesson media and exports to a downloadable excel
            file.
          </Typography>
        </div>
        <div className="col-span-4 text-right self-center">
          <Button
            variant="outlined"
            onClick={() => exportMedia()}
            endIcon={
              <BASESvgIcon className="" size={20}>
                heroicons-solid:download
              </BASESvgIcon>
            }
          >
            Excel
          </Button>
        </div>

        <div className="col-span-12 my-16 border-b" />

        <div className="col-span-8">
          <Typography className="text-lg font-medium">eMentor Notes</Typography>
          <Typography className="mt-8 text-md" color="text.secondary">
            Gathers all of the lesson eMentor notes and exports to a
            downloadable excel file.
          </Typography>
        </div>
        <div className="col-span-4 text-right self-center">
          <Button
            variant="outlined"
            onClick={() => exportEMentors()}
            endIcon={
              <BASESvgIcon className="" size={20}>
                heroicons-solid:download
              </BASESvgIcon>
            }
          >
            Excel
          </Button>
        </div>

        <div className="col-span-12 my-16 border-b" />

        <div className="col-span-8">
          <Typography className="text-lg font-medium">Focus Points</Typography>
          <Typography className="mt-8 text-md" color="text.secondary">
            Gathers all of the lesson Focus Points and exports to a downloadable
            excel file.
          </Typography>
        </div>
        <div className="col-span-4 text-right self-center">
          <Button
            variant="outlined"
            onClick={() => exportFocusPoints()}
            endIcon={
              <BASESvgIcon className="" size={20}>
                heroicons-solid:download
              </BASESvgIcon>
            }
          >
            Excel
          </Button>
        </div>

        <div className="col-span-12 my-16 border-b" />
      </div>
    </div>
  );
}
